(() => {
  /*
    ============================================================
    UNISOURCE LIVE-SITE SEARCH GUARD
    ============================================================
    This production search asset contains guarded routing behavior.
    Any block marked LOCKED / CRITICAL must remain functionally
    unchanged unless the requested update explicitly targets it.
    Make unrelated changes around guarded blocks and preserve the
    latest verified working route/behavior.
    ============================================================
  */

  const SELECTOR = '[data-unisource-search]';
  const MIN_QUERY = 2;
  const LIMIT_PER_GROUP = 8;
  const MIN_PRODUCT_SCORE = 25;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function debounce(fn, wait = 260) {
    let timer = null;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn(...args), wait);
    };
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function cleanQuery(value) {
    return String(value || '')
      .trim()
      .replace(/^["'“”‘’]+|["'“”‘’]+$/g, '')
      .replace(/\s+/g, ' ')
      .toLowerCase();
  }

  function compact(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  function normalizeArray(payload) {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.products)) return payload.products;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.results)) return payload.results;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.designs)) return payload.designs;
    if (Array.isArray(payload?.artwork)) return payload.artwork;
    return [];
  }

  function productFields(item) {
    const id = item.productId || item.ProductID || item.productID || item.id || item.Id || item.product_id || '';
    const title = item.name || item.title || item.productName || item.ProductName || item.Name || '';
    const brand = item.brand || item.manufacturer || item.Manufacturer || item.vendor || '';
    const sku = item.sku || item.styleNumber || item.StyleNumber || item.manufacturerSku || item.ManufacturerSku || item.style || item.Style || '';
    const productNumber = item.productNumber || item.ProductNumber || item.itemNumber || item.ItemNumber || '';
    const description = item.description || item.Description || item.shortDescription || '';
    const keywords = item.keywords || item.tags || '';

    return {
      id: String(id),
      title: String(title),
      brand: String(brand),
      sku: String(sku),
      productNumber: String(productNumber),
      description: String(description),
      keywords: Array.isArray(keywords) ? keywords.join(' ') : String(keywords)
    };
  }

  function scoreProduct(item, rawQuery) {
    const q = cleanQuery(rawQuery);
    const qCompact = compact(q);
    const tokens = q.split(' ').filter(Boolean);

    if (!q || !qCompact) return 0;

    const f = productFields(item);

    const title = f.title.toLowerCase();
    const brand = f.brand.toLowerCase();
    const sku = f.sku.toLowerCase();
    const productNumber = f.productNumber.toLowerCase();
    const id = f.id.toLowerCase();
    const description = f.description.toLowerCase();
    const keywords = f.keywords.toLowerCase();

    const skuCompact = compact(f.sku);
    const productNumberCompact = compact(f.productNumber);
    const idCompact = compact(f.id);
    const titleCompact = compact(f.title);
    const brandCompact = compact(f.brand);
    const descriptionCompact = compact(f.description);
    const keywordsCompact = compact(f.keywords);

    let score = 0;

    /*
      Highest priority:
      exact SKU / style / item number / product ID matches.
      Example: 5250 should strongly return Hanes 5250.
    */
    if (skuCompact && skuCompact === qCompact) score += 140;
    if (productNumberCompact && productNumberCompact === qCompact) score += 135;
    if (idCompact && idCompact === qCompact) score += 120;

    if (skuCompact && skuCompact.startsWith(qCompact)) score += 105;
    if (productNumberCompact && productNumberCompact.startsWith(qCompact)) score += 100;
    if (idCompact && idCompact.startsWith(qCompact)) score += 90;

    if (skuCompact && skuCompact.includes(qCompact)) score += 80;
    if (productNumberCompact && productNumberCompact.includes(qCompact)) score += 75;
    if (idCompact && idCompact.includes(qCompact)) score += 65;

    /*
      Product name match.
      This is the second priority after SKU/style number.
    */
    if (title === q) score += 100;
    if (title.startsWith(q)) score += 80;
    if (title.includes(q)) score += 65;
    if (titleCompact.includes(qCompact)) score += 55;

    /*
      Brand/manufacturer match is useful, but should not overpower SKU/name.
    */
    if (brand === q) score += 55;
    if (brand.startsWith(q)) score += 42;
    if (brand.includes(q)) score += 32;
    if (brandCompact.includes(qCompact)) score += 25;

    /*
      Description/keywords are lower priority.
      They help product-category searches like "polo" or "hoodie"
      but should not create broad noise for numeric SKU searches.
    */
    const queryIsNumeric = /^[0-9]+$/.test(qCompact);

    if (!queryIsNumeric) {
      if (description.includes(q)) score += 18;
      if (descriptionCompact.includes(qCompact)) score += 14;
      if (keywords.includes(q)) score += 18;
      if (keywordsCompact.includes(qCompact)) score += 14;
    }

    /*
      Token matching for multi-word searches.
      Every query token must match at least one strong product field,
      otherwise the product should not appear for that query.
    */
    if (tokens.length > 1) {
      const strongText = `${title} ${brand} ${sku} ${productNumber} ${id}`.toLowerCase();
      const matchedTokens = tokens.filter((token) => strongText.includes(token));

      if (matchedTokens.length === tokens.length) {
        score += 35 + matchedTokens.length * 8;
      } else {
        score -= 40;
      }
    }

    /*
      For numeric searches, be very strict.
      If the number is not in SKU/style/product number/product ID/title,
      do not show it even if the endpoint returned it.
    */
    if (queryIsNumeric) {
      const numericStrongMatch =
        skuCompact.includes(qCompact) ||
        productNumberCompact.includes(qCompact) ||
        idCompact.includes(qCompact) ||
        titleCompact.includes(qCompact);

      if (!numericStrongMatch) return 0;
    }

    return score;
  }

  /*
    ============================================================
    LOCKED / CRITICAL: SEARCH → PRODUCT DETAIL CONTEXT
    ============================================================
    Search results should open the SAME current Product Detail V2
    implementation used by the live catalog, including the product's
    default InkSoft style/color when those values are available.

    Do not remove view=unisource-product-detail-v2.
    Do not revert to /pages/inksoft-product.
    ============================================================
  */
  function getProductDefaultStyle(item) {
    const raw = item?.raw || item?.Raw || {};
    const styles =
      item?.styles ||
      item?.Styles ||
      raw?.styles ||
      raw?.Styles ||
      [];

    return Array.isArray(styles) && styles.length ? (styles[0] || null) : null;
  }

  function getProductStyleId(item) {
    const style = getProductDefaultStyle(item);

    return String(
      style?.id ||
      style?.ID ||
      style?.Id ||
      style?.styleId ||
      style?.StyleId ||
      style?.StyleID ||
      style?.productStyleId ||
      style?.ProductStyleId ||
      style?.ProductStyleID ||
      style?.inksoftStyleId ||
      style?.InkSoftStyleID ||
      ''
    );
  }

  function getProductSelectedColor(item) {
    const style = getProductDefaultStyle(item);

    return String(
      style?.Name ||
      style?.name ||
      style?.ColorName ||
      style?.colorName ||
      style?.Color ||
      style?.color ||
      item?.selectedColor ||
      item?.SelectedColor ||
      item?.color ||
      item?.Color ||
      ''
    ).trim();
  }

  function normalizeProduct(item) {
    const f = productFields(item);
    const image =
      item.imageUrl ||
      item.thumbnailUrl ||
      item.thumbnail ||
      item.primaryImageUrl ||
      item.primaryImage ||
      item.image ||
      '';

    const skuParts = [f.brand, f.sku || f.productNumber].filter(Boolean);

    /*
      ============================================================
      LOCKED / CRITICAL: INKSOFT SEARCH RESULT PRODUCT ROUTING
      VERIFIED LIVE ROUTE BASELINE — 2026-09-02
      ============================================================

      InkSoft product search results MUST route through the current
      Shopify Product Detail V2 VIEW, matching the live catalog pattern:

        /pages/product-detail-v2
          ?view=unisource-product-detail-v2
          &productId=<INKSOFT_PRODUCT_ID>
          &styleId=<DEFAULT_STYLE_ID when available>
          &selectedColor=<DEFAULT_COLOR when available>
          &returnUrl=/pages/product-catalog

      IMPORTANT: the `view=unisource-product-detail-v2` parameter is
      REQUIRED. Without it Shopify loads the legacy/basic page template
      that only displays "Product Detail V2" instead of the live custom
      InkSoft product-detail implementation.

      DO NOT restore /pages/inksoft-product and do NOT blindly trust
      item.url returned by the search endpoint for InkSoft products.
      Older/stale endpoint URLs can point to retired Shopify routes and
      send customers to the custom 404 page.

      The Product Detail V2 controller/catalog flow uses productId as
      the canonical InkSoft product identifier.

      Unrelated search scoring, rendering, design-result, or local-page
      changes must work around this routing block, not through it.
      ============================================================
    */
    const productUrl = (() => {
      if (!f.id) return '/pages/product';

      const url = new URL('/pages/product-detail-v2', window.location.origin);
      const styleId = getProductStyleId(item);
      const selectedColor = getProductSelectedColor(item);

      url.searchParams.set('view', 'unisource-product-detail-v2');
      url.searchParams.set('productId', f.id);

      if (styleId) {
        url.searchParams.set('styleId', styleId);
      }

      if (selectedColor) {
        url.searchParams.set('selectedColor', selectedColor);
      }

      /*
        Search does not originate from a single catalog category, so use the
        live catalog root as the safe Back-to-Products destination.
      */
      url.searchParams.set('returnUrl', '/pages/product-catalog');

      return url.pathname + url.search;
    })();

    return {
      type: 'InkSoft Product',
      title: f.title || 'InkSoft Product',
      description: skuParts.join(' · ') || 'View product details',
      url: productUrl,
      image,
      initials: 'P',
      score: item.__score || 0
    };
  }

  function normalizeDesign(item) {
    const id = item.designId || item.DesignID || item.id || item.Id || item.design_id;
    const title = item.title || item.name || item.designName || item.DesignName || 'Design Idea';
    const category = item.category || item.segment || item.collection || item.theme || 'Design inspiration';
    const image =
      item.thumbnailUrl ||
      item.thumbnail ||
      item.imageUrl ||
      item.artworkUrl ||
      item.previewUrl ||
      item.image ||
      '';

    return {
      type: 'Design Idea',
      title,
      description: category,
      url: item.url || (id ? `/pages/design-inspiration-center?designId=${encodeURIComponent(id)}` : '/pages/design-inspiration-center'),
      image,
      initials: 'D'
    };
  }

  function matchLocalPages(pages, query) {
    const q = cleanQuery(query);

    return pages
      .map((item) => {
        const text = `${item.title} ${item.description} ${item.keywords} ${item.type}`.toLowerCase();
        let score = 0;

        if ((item.title || '').toLowerCase() === q) score += 100;
        if ((item.title || '').toLowerCase().includes(q)) score += 70;
        if (text.includes(q)) score += 35;

        return { item, score };
      })
      .filter((row) => row.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, LIMIT_PER_GROUP)
      .map(({ item }) => ({
        type: item.type || 'Page',
        title: item.title,
        description: item.description,
        url: item.url,
        image: '',
        initials: (item.title || 'U').slice(0, 1).toUpperCase()
      }));
  }

  async function fetchJson(url, query, limit = 40) {
    if (!url) return [];

    const endpoint = new URL(url, window.location.origin);

    endpoint.searchParams.set('q', query);
    endpoint.searchParams.set('search', query);
    endpoint.searchParams.set('query', query);
    endpoint.searchParams.set('sku', query);
    endpoint.searchParams.set('name', query);
    endpoint.searchParams.set('limit', String(limit));

    const response = await fetch(endpoint.toString(), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });

    if (!response.ok) throw new Error(`Search failed: ${response.status}`);

    return normalizeArray(await response.json());
  }

  function renderItem(item) {
    const thumb = item.image
      ? `<img src="${escapeHtml(item.image)}" alt="" loading="lazy">`
      : `<span>${escapeHtml(item.initials || 'U')}</span>`;

    return `
      <a class="unisource-search__item" href="${escapeHtml(item.url)}">
        <span class="unisource-search__thumb" aria-hidden="true">${thumb}</span>
        <span class="unisource-search__meta">
          <span class="unisource-search__type">${escapeHtml(item.type)}</span>
          <span class="unisource-search__title">${escapeHtml(item.title)}</span>
          <span class="unisource-search__description">${escapeHtml(item.description || '')}</span>
        </span>
      </a>
    `;
  }

  function renderSection(title, items) {
    if (!items.length) return '';

    return `
      <section class="unisource-search__section">
        <h3 class="unisource-search__section-heading">${escapeHtml(title)}</h3>
        <div class="unisource-search__list">
          ${items.map(renderItem).join('')}
        </div>
      </section>
    `;
  }

  function dedupeByUrl(items) {
    const seen = new Set();

    return items.filter((item) => {
      const key = item.url || item.title;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  ready(() => {
    document.querySelectorAll(SELECTOR).forEach((root) => {
      const input = root.querySelector('[data-search-input]');
      const results = root.querySelector('[data-search-results]');
      const intro = root.querySelector('[data-search-intro]');
      const clear = root.querySelector('[data-search-clear]');
      const form = root.querySelector('[data-search-form]');
      const pagesScript = root.querySelector('[data-search-pages]');

      let pages = [];
      try {
        pages = JSON.parse(pagesScript?.textContent || '[]');
      } catch (error) {
        pages = [];
      }

      const productsEndpoint = root.dataset.productsEndpoint || '';
      const productsFallbackEndpoint = root.dataset.productsFallbackEndpoint || '';
      const designsEndpoint = root.dataset.designsEndpoint || '';

      function setIntro(show) {
        if (intro) intro.hidden = !show;
      }

      function setClear(show) {
        if (clear) clear.hidden = !show;
      }

      async function runSearch() {
        const rawQuery = (input?.value || '').trim();
        const query = cleanQuery(rawQuery);

        setClear(Boolean(rawQuery));

        if (query.length < MIN_QUERY) {
          setIntro(true);
          results.innerHTML = '';
          return;
        }

        setIntro(false);
        results.innerHTML = '<div class="unisource-search__message">Searching Unisource Apparel…</div>';

        const localResults = matchLocalPages(pages, query);

        const [productsResult, designsResult] = await Promise.allSettled([
          fetchJson(productsEndpoint, query).catch(() => fetchJson(productsFallbackEndpoint, query)),
          fetchJson(designsEndpoint, query)
        ]);

        let products = [];

        if (productsResult.status === 'fulfilled') {
          products = normalizeArray(productsResult.value)
            .map((item) => {
              item.__score = scoreProduct(item, query);
              return item;
            })
            .filter((item) => item.__score >= MIN_PRODUCT_SCORE)
            .sort((a, b) => b.__score - a.__score)
            .slice(0, LIMIT_PER_GROUP)
            .map(normalizeProduct);

          products = dedupeByUrl(products);
        }

        const designs =
          designsResult.status === 'fulfilled'
            ? normalizeArray(designsResult.value).slice(0, LIMIT_PER_GROUP).map(normalizeDesign)
            : [];

        const html = [
          renderSection('InkSoft Products', products),
          renderSection('Design Ideas', designs),
          renderSection('Pages & Resources', localResults)
        ].filter(Boolean).join('');

        if (html) {
          results.innerHTML = html;
          return;
        }

        const productNote = productsResult.status === 'rejected'
          ? '<br><br><strong>Note:</strong> InkSoft product search endpoint is not responding yet. Confirm the Azure/App Proxy product search URL.'
          : '';

        results.innerHTML = `
          <div class="unisource-search__message">
            No results found for <strong>${escapeHtml(rawQuery)}</strong>.${productNote}
          </div>
        `;
      }

      const debouncedSearch = debounce(runSearch, 260);

      input?.addEventListener('input', debouncedSearch);

      clear?.addEventListener('click', () => {
        input.value = '';
        input.focus();
        setClear(false);
        setIntro(true);
        results.innerHTML = '';
      });

      form?.addEventListener('submit', (event) => {
        event.preventDefault();
        runSearch();
      });
    });
  });
})();